import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  Output,
  QueryList,
  ViewChildren,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
    standalone: false,
  selector: "app-otp-input",
  templateUrl: "./otp-input.component.html",
  styleUrls: ["./otp-input.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpInputComponent),
      multi: true
    }
  ]
})
export class OtpInputComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChildren("otpBox") otpBoxes: QueryList<ElementRef>;
  @Output() otpChange = new EventEmitter<string>();

  length = 6;
  digits: string[] = ["", "", "", "", "", ""];
  indexes: number[] = [0, 1, 2, 3, 4, 5];
  disabled = false;

  private onChange: (value: string) => void = function () {};
  onTouched: () => void = function () {};

  constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.focusFirst();
    }, 0);
  }

  writeValue(value: string): void {
    const digitsOnly = this.extractDigits(value);
    for (let i = 0; i < this.length; i++) {
      this.digits[i] = digitsOnly.charAt(i) || "";
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getValue(): string {
    return this.digits.join("");
  }

  isComplete(): boolean {
    return this.getValue().length === this.length;
  }

  focusFirst(): void {
    this.focusIndex(0);
  }

  reset(): void {
    for (let i = 0; i < this.length; i++) {
      this.digits[i] = "";
    }
    this.emitValue();
    this.focusFirst();
  }

  onInput(event: any, index: number): void {
    if (this.disabled) {
      return;
    }
    const raw = event && event.target ? String(event.target.value) : "";
    const digitsOnly = this.extractDigits(raw);
    if (digitsOnly.length > 1) {
      this.fillFromText(digitsOnly, index);
      return;
    }
    if (!digitsOnly) {
      this.digits[index] = "";
      this.emitValue();
      return;
    }
    this.digits[index] = digitsOnly.charAt(0);
    if (event && event.target) {
      event.target.value = this.digits[index];
    }
    this.emitValue();
    if (index < this.length - 1) {
      this.focusIndex(index + 1);
    }
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    if (this.disabled) {
      return;
    }
    const key = event.key;
    if (key === "Backspace") {
      event.preventDefault();
      if (this.digits[index]) {
        this.digits[index] = "";
      } else if (index > 0) {
        this.digits[index - 1] = "";
        this.focusIndex(index - 1);
      }
      this.emitValue();
      return;
    }
    if (key === "Delete") {
      event.preventDefault();
      this.digits[index] = "";
      this.emitValue();
      return;
    }
    if (key === "ArrowLeft" && index > 0) {
      this.focusIndex(index - 1);
      event.preventDefault();
      return;
    }
    if (key === "ArrowRight" && index < this.length - 1) {
      this.focusIndex(index + 1);
      event.preventDefault();
      return;
    }
    if (key === "Tab") {
      return;
    }
    if (key >= "0" && key <= "9") {
      event.preventDefault();
      this.digits[index] = key;
      this.emitValue();
      if (index < this.length - 1) {
        this.focusIndex(index + 1);
      }
      return;
    }
    if (key.length === 1 && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent, index: number): void {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    const pasted = event.clipboardData ? event.clipboardData.getData("text") : "";
    this.fillFromText(this.extractDigits(pasted), index);
  }

  onFocus(event: any): void {
    if (event && event.target && event.target.select) {
      event.target.select();
    }
  }

  trackByIndex(index: number, value: number): number {
    return value;
  }

  private fillFromText(digitsOnly: string, startIndex: number): void {
    if (!digitsOnly) {
      return;
    }
    let cursor = startIndex;
    for (let i = 0; i < digitsOnly.length && cursor < this.length; i++) {
      this.digits[cursor] = digitsOnly.charAt(i);
      cursor++;
    }
    this.emitValue();
    const nextEmpty = this.digits.indexOf("");
    if (nextEmpty >= 0) {
      this.focusIndex(nextEmpty);
    } else {
      this.focusIndex(this.length - 1);
    }
  }

  private extractDigits(value: any): string {
    return String(value || "").replace(/\D/g, "").substring(0, this.length);
  }

  private emitValue(): void {
    const value = this.digits.join("");
    const push = () => {
      this.onChange(value);
      this.otpChange.emit(value);
      this.cdr.markForCheck();
    };
    if (NgZone.isInAngularZone()) {
      push();
    } else {
      this.ngZone.run(push);
    }
  }

  private focusIndex(index: number): void {
    if (!this.otpBoxes) {
      return;
    }
    const box = this.otpBoxes.toArray()[index];
    if (box && box.nativeElement) {
      box.nativeElement.focus();
      if (box.nativeElement.select) {
        box.nativeElement.select();
      }
    }
  }
}
